/**
 * CalendarCell — single day cell.
 *
 *   <CalendarCell label="Pondělí" date="27. dub." today marker="— Dnes">
 *     <EventChip tone="accent" title="Náhradní vozidlo" />
 *     <EventChip tone="warn"   title="Stezka" sub="…" />
 *   </CalendarCell>
 *
 * Used inside `<Calendar.MonthGrid>` (one cell per day) or as a standalone
 * day pane. Header label + date sit on top; children stack inline as event
 * chips. `today` paints the date in accent and adds an accent rule. `muted`
 * is for days from neighbouring months in the month view.
 *
 * Source: .agents/design/project/artboard-radical-plan.jsx:94-116 (date head)
 */

import type { ReactNode } from 'react';

interface CalendarCellProps {
  /** Day-of-week label (e.g. "Pondělí"). Optional — month grids may put it in the header row instead. */
  label?: ReactNode;
  /** Date label (e.g. "27" or "27. dub."). */
  date: ReactNode;
  /** Today highlight — accent date + accent under-rule. */
  today?: boolean;
  /** Mono uppercase eyebrow under the date (e.g. "— Dnes"). */
  marker?: ReactNode;
  /** Days from prev/next month in month view — dim the cell. */
  muted?: boolean;
  /** Click target — when set, the whole cell becomes a button. */
  onClick?: () => void;
  children?: ReactNode;
  className?: string;
  /** Min height for the cell body. Defaults to `auto`. */
  minHeight?: number | string;
}

export function CalendarCell({
  label,
  date,
  today = false,
  marker,
  muted = false,
  onClick,
  children,
  className = '',
  minHeight,
}: CalendarCellProps) {
  const interactive = !!onClick;

  return (
    <div
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick!();
              }
            }
          : undefined
      }
      style={minHeight !== undefined ? { minHeight } : undefined}
      className={
        'relative flex flex-col gap-1.5 border-r border-b border-line bg-paper p-2 last:border-r-0 ' +
        (today ? 'bg-accent-soft ' : '') +
        (muted ? 'opacity-50 ' : '') +
        (interactive
          ? 'cursor-pointer transition-colors hover:bg-surface-2 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent '
          : '') +
        className
      }
    >
      <div className="flex items-baseline justify-between gap-2">
        <div className="flex flex-col gap-0.5">
          {label && (
            <span
              className={
                'font-mono text-[9.5px] uppercase tracking-[0.1em] ' +
                (today ? 'text-accent-text' : 'text-ink-3')
              }
            >
              {label}
            </span>
          )}
          <span
            className={
              'text-[14px] font-bold leading-none tracking-[-0.01em] ' +
              (today ? 'text-accent-text' : 'text-ink')
            }
          >
            {date}
          </span>
        </div>
        {marker && (
          <span className="font-mono text-[9px] font-semibold uppercase tracking-[0.12em] text-accent-text">
            {marker}
          </span>
        )}
      </div>
      {children && (
        <div className="flex flex-1 flex-col gap-1 overflow-hidden">{children}</div>
      )}
      {today && (
        <span
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-px bg-accent"
        />
      )}
    </div>
  );
}
